import React, { useState } from 'react';
import { Table, Tooltip, Button, message } from 'antd';
import { InfoCircleOutlined, CheckCircleFilled } from '@ant-design/icons';
import { PaymentData } from '../../types/main';
import { ColumnsType } from 'antd/es/table';
import { NullableStatusResponse } from '../../helpers/statusUtils';
import useScreenSizeHook from '../../../../utils/useScreenSizeHook';
import { CgDanger } from 'react-icons/cg';
import { authAxios } from '../../../../utils/session_utils';



export interface AllocationDetailsTableProps {
  unappliedPaymentData: PaymentData[];
  setUnAppliedPaymentData: (data: PaymentData[]) => void;
  handleAllocate: (keys: string[]) => Promise<boolean>;
  onRecordSelect: (record: PaymentData) => void;
  selectedRecord: PaymentData | null;
  allocationStatus: string;

}

const AllocationDetailsTable: React.FC<AllocationDetailsTableProps> = ({ 
  unappliedPaymentData, 
  setUnAppliedPaymentData,
  handleAllocate,
  onRecordSelect,
  selectedRecord,
  allocationStatus,
}) => {
  const { height } = useScreenSizeHook();
  const [isAllocatingAll, setIsAllocatingAll] = useState(false);

  const handleAllocateAll = async () => {
    setIsAllocatingAll(true);
    const allKeys = unappliedPaymentData
      .filter(record => record.linkedToViewership && record.linkedViewershipPool)
      .map(record => record.key);
    
  
    try {
      await handleAllocate(allKeys);
    } finally {
      setIsAllocatingAll(false);
    }
    };
    
    

const getButtonColor = (record: PaymentData, status: string) => {
  if (record.linkedToViewership && record.linkedViewershipPool) {
    if (status === 'initial') {
      return '#1890ff';  // Blue: Ready to allocate
    } else {
      switch (status) {
        case 'processing': return '#faad14';  // Yellow: Processing
        case 'success': return '#52c41a';  // Green: Success
        case 'failure': return '#f5222d';  // Red: Failure
        default: return '#1890ff';  // Blue: Ready (fallback)
      }
    }
  } else {
    return '#d9d9d9';  // Grey: Not ready
  }
};

  const getAllocationLevel = (strategy: PaymentData['allocationStrategy']): string => {
    if (!strategy) return 'Not Set';
    const { platform, partner, channel, territory } = strategy;
    const selectedFields = [
      platform && 'Platform',
      partner && 'Partner',
      channel && 'Channel',
      territory && 'Territory'
    ].filter(Boolean);
    return selectedFields.length > 0 ? selectedFields.join(' + ') : 'Custom';
  };
  const columns: ColumnsType<PaymentData> = [
    {
      title: '',
      key: 'selection',
      width: 50,
      render: (_: any, record: PaymentData) => (
        <div style={{ textAlign: 'center' }}>
          {record.key === selectedRecord?.key ? (
            <CheckCircleFilled style={{ color: '#1890ff', fontSize: '18px' }} />
          ) : (
            <div style={{
              width: '16px',
              height: '18px',
              borderRadius: '50%',
              backgroundColor: '#d9d9d9',
              display: 'inline-block'
            }} />
          )}
        </div>
      ),
    },
    {
      title: () => (
        <div>
          Record Summary {' '}
          <Tooltip title="Composed of (Register Name or Partner) + Period(if available) + Label(Expense/Revenue)">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Tooltip>
        </div>
      ),
      dataIndex: 'registerName',
      key: 'summary',
      width: 200,
      render: (text: string, record: PaymentData) => {
        const partIdentifier = record.registerName || record.partner || 'Unnamed Record';
        const year = record.year || '';
        const quarter = record.quarter || '';
        const month = record.month || '';
        const channel = record.channel || '';
        const period = [year, quarter, month].filter(Boolean).join('-');
        const summary = `${partIdentifier}${period ? ` (${period})` : ''}${channel ? ` - ${channel}` : ''} - ${record.label}`;
        const isOnO = record.domain?.toLowerCase() === "owned and operated"
        const detailsTooltip = (
          <div>
            {isOnO && !record.departmentId && <p style={{color: "red"}}>Department ID not set. Wait for normalization</p>}
            {!isOnO && !record.dealParent && <p style={{color: "red"}}>Deal Parent not set. Wait for normalization</p>}
            <p><strong>Register Name:</strong> {record.registerName}</p>
            <p><strong>Partner:</strong> {record.partner}</p>
            <p><strong>File:</strong> {record.filename}</p>
            <p><strong>Payment Date:</strong> {record.paymentDate}</p>
            <p><strong>Channel(Id):</strong> {record.channel}({record.channelId})</p>
            <p><strong>Territory(Id):</strong> {record.territory}({record.territoryId})</p>
            <p><strong>Platform:</strong> {record.platform}</p>
            <p><strong>Domain:</strong> {record.domain}</p>
            {isOnO && <p><strong>Department(Id):</strong> {record.department}({record.departmentId ?? ''})</p>}
            {!isOnO && <p><strong>Deal Parent:</strong> {record.dealParent || 'Wait for normalization'}</p>}
            <p><strong>Label:</strong> {record.label}</p>
            <p><strong>Amount:</strong> {record.amount}</p>
          </div>
        );

        return (
          <Tooltip title={detailsTooltip} placement="right">
            <div 
              style={{ 
                cursor: 'pointer',
                padding: '8px',
                background: '#f0f2f5',
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
                fontWeight: 'bold',
                color: '#1890ff'
              }}
            >
              {summary} {" "}
              {((isOnO && !record.departmentId) || (!isOnO && !record.dealParent)) && <CgDanger style={{color: "red"}} />}
            </div>
          </Tooltip>
        );
      },
    },    
    {
      title: 'Allocation Level',
      dataIndex: 'allocationStrategy',
      key: 'allocationLevel',
      width: 210,
      render: (strategy: PaymentData['allocationStrategy']) => (
        <div>{getAllocationLevel(strategy)}</div>
      ),
    },
    {
      title: 'Allocation Period',
      dataIndex: 'allocationPeriod',
      key: 'allocationPeriod',
      width: 150,
      render: (text: string) => <div>{text || 'Not Set'}</div>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 200,
      render: (_: any, record: PaymentData) => {
        const isReady = record.linkedToViewership && record.linkedViewershipPool;
        const isProcessing = allocationStatus === 'processing';
        
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                console.log('Record being allocated:', record);
                console.log('Key being sent:', record.key);
                handleAllocate([record.key]);
              }}              
              disabled={!isReady || isProcessing}
              style={{ 
                backgroundColor: getButtonColor(record, allocationStatus),
                border: 'none',
                color: 'white',
                padding: '5px 10px',
                borderRadius: '4px',
                cursor: (!isReady || isProcessing) ? 'not-allowed' : 'pointer'
              }}
            >
              {isProcessing ? 'Processing...' : 'Allocate'}
            </button>
            <button
              onClick={async (e) => {
                e.stopPropagation();

                if (!record.id) {
                  message.error('Cannot remove record: missing ID');
                  return;
                }

                try {
                  const response = await authAxios({
                    method: 'PUT',
                    url: `${process.env.REACT_APP_BACK_END_API}/register-data/payments/flag-deletion`,
                    data: { id: record.id }
                  });

                  if (response.data.success) {
                    message.success('Payment record flagged for deletion');
                    // Remove from UI
                    setUnAppliedPaymentData(unappliedPaymentData.filter(item => item.key !== record.key));
                    if (selectedRecord?.key === record.key) {
                      onRecordSelect(null as any);
                    }
                  } else {
                    message.error('Failed to flag payment for deletion');
                  }
                } catch (error: any) {
                  console.error('Error flagging payment for deletion:', error);
                  message.error(error?.response?.data?.message || 'Error flagging payment for deletion');
                }
              }}
              style={{
                backgroundColor: '#f5222d',
                border: 'none',
                color: 'white',
                padding: '5px 10px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Remove
            </button>
          </div>
        );
      },
    }
  ];

  return (
    <>
    <Table
      dataSource={unappliedPaymentData
        .filter(record => record.toBeProcessed)
        .map(record => ({
          ...record,
          tableKey: `allocation-${record.key}`
        }))
      }      
      rowKey="tableKey"  
      columns={columns}
      pagination={false}
      scroll={{ x: 'max-content', y: height - 290 }}
      onRow={(record) => ({
        onClick: () => onRecordSelect(record),
        style: {
          background: record.key === selectedRecord?.key ? '#e6f7ff' : 'transparent',
          cursor: 'pointer'
        }
      })}
    />
    <Button
        onClick={handleAllocateAll}
        disabled={isAllocatingAll || allocationStatus === 'processing'}
        style={{
          marginTop: '16px',
          backgroundColor: '#1890ff',
          color: 'white',
        }}
      >
        {isAllocatingAll ? 'Allocating All...' : 'Allocate All'}
      </Button>
    </>
  );
};

export default AllocationDetailsTable;